import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, putData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { GOOGLE_MAPS_CONFIG } from "../../../config/keys.ts";
import CategoryModal from "../../../Modal/CategoryModal/CategoryModal";
import SubcategoryModal from "../../../Modal/SubcategoryModal/SubcategoryModal";
import VehicleTypeModal from "../../../Modal/VehicleTypeModal/VehicleTypeModal";

// Marker component
const Marker = ({ position, map }) => {
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    if (!marker && map) {
      setMarker(new window.google.maps.Marker({ position, map }));
    }

    // Remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker, map, position]);

  return null;
};

// Simple debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
};

const render = (status) => {
  if (status === Status.LOADING) return <div>Loading map...</div>;
  if (status === Status.FAILURE) return <div>Failed to load map</div>;
  return null;
};

const Map = ({ center, zoom, onMapClick, locations = [] }) => {
  const ref = React.useRef(null);
  const [map, setMap] = React.useState(null);

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(
        new window.google.maps.Map(ref.current, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_TOP,
          },
        })
      );
    }
  }, [ref, map, center, zoom]);

  // Update map center and zoom when they change
  React.useEffect(() => {
    if (map) {
      map.setCenter(center);
      map.setZoom(zoom);
    }
  }, [map, center, zoom]);

  // Add click listener
  React.useEffect(() => {
    if (map && onMapClick) {
      const listener = map.addListener("click", (e) => {
        onMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });
      return () => window.google.maps.event.removeListener(listener);
    }
  }, [map, onMapClick]);

  return (
    <div
      ref={ref}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      {map &&
        locations.map(
          (loc, index) =>
            loc.coordinates && (
              <Marker
                key={`marker-${index}`}
                position={loc.coordinates}
                map={map}
              />
            )
        )}
    </div>
  );
};

export default function AddReward() {
  const navigate = useNavigate();
  const location = useLocation();
  const { rewardData } = location.state || {};
  const [errors, setErrors] = useState({
    percentage: "",
    redeemAfterDays: "",
  });

  const [formData, setFormData] = useState({
    percentage: "",
    redeemAfterDays: "",
    category_id: null,
    subCategory_id: null,
    vehicle_id: null,
    isActive: true,
    locations: [],
  });

  const [selectedLocation, setSelectedLocation] = useState({
    applyToAll: false,
    city: "",
    state: "",
    pincode: "",
    pincodeType: "specific",
    pincodes: [""],
    coordinates: null,
    address: "",
  });

  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [selectedSubCategoryName, setSelectedSubCategoryName] = useState("");
  const [selectedVehicleTypeName, setSelectedVehicleTypeName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [showVehicleTypeModal, setShowVehicleTypeModal] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [suggestedLocations, setSuggestedLocations] = useState([]);
  const [activeLocationIndex, setActiveLocationIndex] = useState(0);

  // Initialize form data from rewardData
  useEffect(() => {
    if (rewardData) {
      console.log("Initializing form with reward data:", rewardData);

      // Format locations for form
      const formattedLocations = Array.isArray(rewardData.locations)
        ? rewardData.locations.map(loc => ({
          applyToAll: loc.applyToAll || false,
          city: loc.city || "",
          state: loc.state || "",
          pincode: loc.pincodes?.[0] || "",
          pincodeType: loc.applyToAll ? "all" : "specific",
          pincodes: Array.isArray(loc.pincodes) ? loc.pincodes : (loc.pincodes ? [loc.pincodes] : []),
          coordinates: loc.coordinates || null,
          address: loc.address || ""
        }))
        : [];

      setFormData({
        percentage: rewardData.percentage?.toString() || "",
        redeemAfterDays: rewardData.redeemAfterDays?.toString() || "",
        category_id: rewardData.category_id?._id || rewardData.category?._id || null,
        subCategory_id: rewardData.subcategory_id?._id || rewardData.subCategory?._id || null,
        vehicle_id: rewardData.vehicle_id?._id || rewardData.vehicle?._id || null,
        isActive: rewardData.isActive ?? true,
        locations: formattedLocations
      });

      // Set display names
      setSelectedCategoryName(rewardData.category_id?.title || rewardData.category?.title || "");
      setSelectedSubCategoryName(rewardData.subcategory_id?.title || rewardData.subCategory?.title || "");
      setSelectedVehicleTypeName(rewardData.vehicle_id?.vehicleName || rewardData.vehicle?.vehicleName || "");
    }
  }, [rewardData]);

  // Handle category selection
  const handleCategorySelect = (category) => {
    setFormData(prev => ({
      ...prev,
      category_id: category._id,
      subCategory_id: null,
      vehicle_id: null,
    }));
    setSelectedCategoryName(category.title);
    setSelectedSubCategoryName("");
    setSelectedVehicleTypeName("");
    setShowCategoryModal(false);
  };

  // Handle subcategory selection
  const handleSubCategorySelect = (subcategory) => {
    setFormData(prev => ({
      ...prev,
      subCategory_id: subcategory._id,
      vehicle_id: null,
    }));
    setSelectedSubCategoryName(subcategory.title);
    setSelectedVehicleTypeName("");
    setShowSubCategoryModal(false);
  };

  const handleVehicleTypeSelect = (vehicleType) => {
    setFormData(prev => ({ ...prev, vehicle_id: vehicleType._id }));
    setSelectedVehicleTypeName(vehicleType.vehicleName);
    setShowVehicleTypeModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // Allow empty input
    if (value === "") {
      setFormData(prev => ({ ...prev, [name]: "" }));
      newErrors[name] = "";
      setErrors(newErrors);
      return;
    }

    let numericValue = Number(value);

    // Validate percentage
    if (name === "percentage") {
      if (numericValue > 100)
        newErrors.percentage = "Percentage cannot exceed 100";
      else newErrors.percentage = "";
    }

    // Validate redeemAfterDays
    if (name === "redeemAfterDays") {
      if (numericValue > 365)
        newErrors.redeemAfterDays = "Days cannot exceed 365";
      else newErrors.redeemAfterDays = "";
    }

    setErrors(newErrors);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Debounced location search
  const searchLocations = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setSuggestedLocations([]);
        return;
      }

      try {
        // First try to search by pincode if the query looks like a pincode
        if (/^\d{6}$/.test(query)) {
          // Use the Indian Postal API for better pincode search
          const response = await fetch(
            `https://api.postalpincode.in/pincode/${query}`
          );
          const data = await response.json();

          if (
            data[0]?.Status === "Success" &&
            data[0]?.PostOffice?.length > 0
          ) {
            const formattedLocations = data[0].PostOffice.map((office) => ({
              display: `${office.Name}, ${office.District}, ${office.State} - ${office.Pincode}`,
              city: office.District || "",
              state: office.State || "",
              pincode: office.Pincode || "",
              address_components: {
                city: office.District,
                state: office.State,
                postcode: office.Pincode,
                country: "India",
              },
              formatted_address: `${office.Name}, ${office.District}, ${office.State} ${office.Pincode}, India`,
            }));
            setSuggestedLocations(formattedLocations);
          } else {
            setSuggestedLocations([]);
            toast.warning("No locations found for this pincode");
          }
        } else {
          // Regular location search with better Indian results
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
              query
            )}&addressdetails=1&limit=5&countrycodes=in&featuretype=city,town,village,suburb`
          );
          const data = await response.json();

          const formattedLocations = data.map((item) => {
            const city =
              item.address.city ||
              item.address.town ||
              item.address.village ||
              "";
            const state = item.address.state || "";
            const pincode = item.address.postcode || "";

            return {
              display: [
                item.address.road,
                item.address.suburb,
                city,
                state,
                pincode,
              ]
                .filter(Boolean)
                .join(", "),
              city: city,
              state: state,
              country: item.address.country || "India",
              pincode: pincode,
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon),
              address_components: item.address,
              formatted_address: [
                item.address.road,
                item.address.suburb,
                city,
                state,
                pincode,
                "India",
              ]
                .filter(Boolean)
                .join(", "),
            };
          });

          setSuggestedLocations(formattedLocations);
        }
      } catch (error) {
        console.error("Error searching locations:", error);
        toast.error("Failed to search locations");
        setSuggestedLocations([]);
      }
    }, 500),
    []
  );

  const handleLocationSearch = (e) => {
    const query = e.target.value;
    setLocationSearch(query);

    // If it's a 6-digit number, it's likely a pincode
    if (/^\d{0,6}$/.test(query)) {
      // Update pincode in selected location
      setSelectedLocation((prev) => ({
        ...prev,
        pincode: query,
      }));

      // Only search if we have exactly 6 digits
      if (query.length === 6) {
        searchLocations(query);
      } else if (query.length > 0 && query.length < 6) {
        // Clear suggestions if we're in the middle of typing a pincode
        setSuggestedLocations([]);
      }
    } else {
      // Regular location search
      if (query.length >= 3) {
        searchLocations(query);
      } else {
        setSuggestedLocations([]);
      }
    }
  };

  const handleSelectLocation = (location) => {
    // Extract pincode from the location data
    let pincode = "";

    // Try to get pincode from different possible locations in the response
    if (location.pincode) {
      pincode = location.pincode;
    } else if (location.address_components?.postcode) {
      pincode = location.address_components.postcode;
    } else if (location.address_components?.postal_code) {
      pincode = location.address_components.postal_code;
    } else if (location.postcode) {
      pincode = location.postcode;
    }

    // Extract city from the location data
    let city = "";
    if (location.city) {
      city = location.city;
    } else if (location.address_components) {
      const address = location.address_components;
      city =
        address.city ||
        address.town ||
        address.village ||
        address.suburb ||
        address.county ||
        "";
    }

    // Extract state from the location data
    let state = "";
    if (location.state) {
      state = location.state;
    } else if (location.address_components?.state) {
      state = location.address_components.state;
    } else if (location.address_components?.region) {
      state = location.address_components.region;
    }

    // Format the display address
    const displayAddress =
      location.display ||
      location.formatted_address ||
      [
        location.address_components?.road,
        location.address_components?.suburb,
        city,
        state,
        pincode,
      ]
        .filter(Boolean)
        .join(", ");

    // Update the selected location with all the details
    setSelectedLocation((prev) => ({
      ...prev,
      city: city.trim(),
      state: state.trim(),
      pincode: pincode.toString().trim(),
      pincodes: pincode ? [pincode.toString()] : [""],
      coordinates:
        location.lat && location.lon
          ? { lat: parseFloat(location.lat), lng: parseFloat(location.lon) }
          : prev.coordinates,
      address: displayAddress,
    }));

    // Update the search input to show the selected location
    setLocationSearch(displayAddress);
    setSuggestedLocations([]);

    // Show success message if pincode was found and set
    if (pincode) {
      toast.success(
        `Location selected. Pincode ${pincode} has been auto-filled.`
      );
    } else {
      toast.info("Location selected. Please enter pincode manually if needed.");
    }
  };

  const handleAddLocation = () => {
    if (!selectedLocation.city || !selectedLocation.state) {
      toast.error("Please select a valid location with city and state");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      locations: [...prev.locations, { ...selectedLocation }],
    }));

    // Reset the selected location
    setSelectedLocation({
      applyToAll: false,
      city: "",
      state: "",
      pincode: "",
      pincodeType: "specific",
      pincodes: [""],
      coordinates: null,
      address: "",
    });
    setLocationSearch("");
  };

  const handleRemoveLocation = (index) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index),
    }));
    toast.success("Location removed successfully");
  };

  const validateForm = () => {
    // Check for errors
    if (errors.percentage || errors.redeemAfterDays) {
      toast.error("Please fix the form errors before submitting.");
      return false;
    }

    // Check required fields
    if (!formData.category_id) {
      toast.error("Please select a category");
      return false;
    }

    if (!formData.percentage || formData.percentage === "") {
      toast.error("Please enter percentage");
      return false;
    }

    if (!formData.redeemAfterDays || formData.redeemAfterDays === "") {
      toast.error("Please enter redeem after days");
      return false;
    }

    // Validate locations
    if (Array.isArray(formData.locations) && formData.locations.length > 0) {
      for (const [index, location] of formData.locations.entries()) {
        if (!location) continue;

        if (!location.city || !location.state) {
          toast.error(`Please fill in both city and state for location ${index + 1}`);
          return false;
        }

        if (location.pincodeType === "specific" &&
          (!Array.isArray(location.pincodes) || location.pincodes.length === 0 || location.pincodes.every(p => !p || p.trim() === ""))) {
          toast.error(`Please add at least one pincode for location ${index + 1} or select 'All Pincodes'`);
          return false;
        }

        // Validate pincode format if pincodes exist
        if (Array.isArray(location.pincodes)) {
          for (const pincode of location.pincodes) {
            if (pincode && pincode.trim() !== "" && !/^\d{6}$/.test(pincode)) {
              toast.error(`Invalid pincode format in location ${index + 1}`);
              return false;
            }
          }
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form data before validation:", formData);

    if (!validateForm()) return;

    try {
      setLoading(true);

      // Format locations for API
      const validLocations = Array.isArray(formData.locations)
        ? formData.locations.map(loc => ({
          applyToAll: loc.pincodeType === "all",
          city: loc.city || "",
          state: loc.state || "",
          pincodeType: loc.pincodeType || "specific",
          pincodes: loc.pincodeType === "all"
            ? []
            : (Array.isArray(loc.pincodes) ? loc.pincodes.filter(p => p && p.trim() !== "") : []),
          coordinates: loc.coordinates || null,
          address: loc.address || ""
        }))
        : [];

      // Prepare the payload according to the API requirements
      const payload = {
        category_id: formData.category_id,
        subcategory_id: formData.subCategory_id || null,
        vehicle_id: formData.vehicle_id || null,
        percentage: Number(formData.percentage) || 0,
        redeemAfterDays: Number(formData.redeemAfterDays) || 0,
        isActive: Boolean(formData.isActive),
        locations: validLocations
      };

      console.log("Sending payload:", payload);

      let response;
      let endpoint;
      let requestData = { ...payload };

      if (rewardData?._id) {
        // For update
        endpoint = "api/reward-config/update";
        requestData = {
          ...payload,
          rewardConfigId: rewardData._id
        };
        console.log("Update request:", requestData);
        response = await putData(endpoint, requestData);
      } else {
        // For create
        endpoint = "api/reward-config";
        console.log("Create request:", requestData);
        response = await postData(endpoint, requestData, "POST");
      }

      console.log("API Response:", response);

      if (response?.success) {
        const message = rewardData?._id
          ? 'Reward updated successfully'
          : 'Reward created successfully';

        toast.success(message);
        navigate("/reward-list");
      } else {
        const errorMessage = response?.data?.message ||
          response?.error ||
          response?.message ||
          'Failed to save reward';
        console.error('API Error Response:', response);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Reward submission error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'An unexpected error occurred while saving the reward';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Back Button */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          {/* Debug Info (remove in production) */}
          {rewardData && (
            <div className="alert alert-info mb-3">
              <h6>Edit Mode - Reward ID: {rewardData._id}</h6>
              <small>Category: {selectedCategoryName} | Subcategory: {selectedSubCategoryName} | Vehicle: {selectedVehicleTypeName}</small>
            </div>
          )}

          {/* Form Card */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    {rewardData ? "Edit Reward" : "Create New Reward"}
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Percentage */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Percentage <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6 mt-4">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          name="percentage"
                          className="form-control"
                          placeholder="Enter Percentage"
                          value={formData.percentage}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                        />
                        {errors.percentage && (
                          <small className="text-danger position-absolute mt-1">
                            {errors.percentage}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Redeem After Days */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3 mt-4">
                        <label className="form-label fw-medium">
                          Redeem After Days{" "}
                          <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="number"
                          min="0"
                          max="365"
                          name="redeemAfterDays"
                          className="form-control"
                          placeholder="Enter Days"
                          value={formData.redeemAfterDays}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                        />
                        {errors.redeemAfterDays && (
                          <small className="text-danger position-absolute  mt-1">
                            {errors.redeemAfterDays}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Category Field */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Category <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6 d-flex">
                        <input
                          type="text"
                          className="form-control"
                          value={selectedCategoryName}
                          readOnly
                          placeholder="Select Category"
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary ms-2"
                          onClick={() => setShowCategoryModal(true)}
                          disabled={loading}
                        >
                          Choose
                        </button>
                      </div>
                    </div>

                    {/* Subcategory Field */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Subcategory
                        </label>
                      </div>
                      <div className="col-md-6 d-flex">
                        <input
                          type="text"
                          className="form-control"
                          value={selectedSubCategoryName}
                          readOnly
                          placeholder={
                            formData.category_id
                              ? "Select Subcategory"
                              : "Select category first"
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary ms-2"
                          onClick={() => setShowSubCategoryModal(true)}
                          disabled={!formData.category_id || loading}
                        >
                          Choose
                        </button>
                      </div>
                    </div>

                    {/* Vehicle Type Field */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Vehicle Type
                        </label>
                      </div>
                      <div className="col-md-6 d-flex align-items-center">
                        <input
                          type="text"
                          className="form-control"
                          value={selectedVehicleTypeName || ""}
                          readOnly
                          placeholder={
                            formData.subCategory_id
                              ? "Select Vehicle Type (Optional)"
                              : "Select subcategory first"
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary ms-2"
                          onClick={() => setShowVehicleTypeModal(true)}
                          disabled={!formData.subCategory_id || loading}
                        >
                          {selectedVehicleTypeName ? "Change" : "Choose"}
                        </button>
                        {selectedVehicleTypeName && (
                          <button
                            type="button"
                            className="btn btn-outline-danger ms-2"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                vehicle_id: null,
                              }));
                              setSelectedVehicleTypeName("");
                            }}
                            disabled={loading}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Single Map for Location Selection */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="mb-0">Add Location</h5>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">Search Location</label>
                          <div className="position-relative">
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search for a location in India (e.g., Andheri East, Mumbai) or enter a 6-digit pincode"
                                value={locationSearch}
                                onChange={handleLocationSearch}
                                disabled={loading}
                              />
                              {locationSearch && (
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => {
                                    setLocationSearch("");
                                    setSuggestedLocations([]);
                                  }}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              )}
                            </div>
                            {suggestedLocations.length > 0 && (
                              <div
                                className="list-group position-absolute w-100 mt-1"
                                style={{ zIndex: 1000 }}
                              >
                                {suggestedLocations.map((suggestion, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="list-group-item list-group-item-action text-start"
                                    onClick={() => handleSelectLocation(suggestion)}
                                  >
                                    {suggestion.display}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-4">
                            <label className="form-label">City</label>
                            <input
                              type="text"
                              className="form-control"
                              value={selectedLocation.city}
                              onChange={(e) =>
                                setSelectedLocation((prev) => ({
                                  ...prev,
                                  city: e.target.value,
                                }))
                              }
                              disabled={loading}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">State</label>
                            <input
                              type="text"
                              className="form-control"
                              value={selectedLocation.state}
                              onChange={(e) =>
                                setSelectedLocation((prev) => ({
                                  ...prev,
                                  state: e.target.value,
                                }))
                              }
                              disabled={loading}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Pincode</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={selectedLocation.pincode || ""}
                                onChange={(e) => {
                                  const value = e.target.value
                                    .replace(/\D/g, "")
                                    .substring(0, 6);
                                  setSelectedLocation((prev) => ({
                                    ...prev,
                                    pincode: value,
                                    pincodes: value ? [value] : [""],
                                  }));
                                }}
                                placeholder={
                                  selectedLocation.pincode
                                    ? ""
                                    : "Will auto-fill from location search"
                                }
                                disabled={loading}
                                maxLength={6}
                                style={{
                                  backgroundColor: selectedLocation.pincode
                                    ? "#f8f9fa"
                                    : "#fff",
                                  fontWeight: selectedLocation.pincode
                                    ? "500"
                                    : "normal",
                                  borderColor: selectedLocation.pincode
                                    ? "#198754"
                                    : "#ced4da",
                                }}
                              />
                              {selectedLocation.pincode && (
                                <span
                                  className="input-group-text bg-success text-white"
                                  style={{ borderColor: "#198754" }}
                                >
                                  <i className="fas fa-check"></i>
                                </span>
                              )}
                            </div>
                            {!selectedLocation.pincode && (
                              <small className="text-muted">
                                Search for a location to auto-fill pincode
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Pincode Type</label>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="pincodeType"
                              id="specific"
                              checked={
                                selectedLocation.pincodeType === "specific"
                              }
                              onChange={() =>
                                setSelectedLocation((prev) => ({
                                  ...prev,
                                  pincodeType: "specific",
                                }))
                              }
                              disabled={loading}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="specific"
                            >
                              Specific Pincodes
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="pincodeType"
                              id="all"
                              checked={selectedLocation.pincodeType === "all"}
                              onChange={() =>
                                setSelectedLocation((prev) => ({
                                  ...prev,
                                  pincodeType: "all",
                                }))
                              }
                              disabled={loading}
                            />
                            <label className="form-check-label" htmlFor="all">
                              All Pincodes in City
                            </label>
                          </div>
                        </div>

                        {selectedLocation.pincodeType === "specific" && (
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label">Pincodes</label>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() =>
                                  setSelectedLocation((prev) => ({
                                    ...prev,
                                    pincodes: [...prev.pincodes, ""],
                                  }))
                                }
                                disabled={loading}
                              >
                                + Add Pincode
                              </button>
                            </div>
                            {selectedLocation.pincodes.map((pincode, index) => (
                              <div key={index} className="input-group mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter 6-digit pincode"
                                  value={pincode}
                                  onChange={(e) => {
                                    const newPincodes = [
                                      ...selectedLocation.pincodes,
                                    ];
                                    newPincodes[index] = e.target.value;
                                    setSelectedLocation((prev) => ({
                                      ...prev,
                                      pincodes: newPincodes,
                                    }));
                                  }}
                                  disabled={loading}
                                  pattern="\d{6}"
                                  maxLength={6}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => {
                                    const newPincodes =
                                      selectedLocation.pincodes.filter(
                                        (_, i) => i !== index
                                      );
                                    setSelectedLocation((prev) => ({
                                      ...prev,
                                      pincodes: newPincodes.length
                                        ? newPincodes
                                        : [""],
                                    }));
                                  }}
                                  disabled={loading}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="mt-3">
                          <label className="form-label">
                            Map Location (Click to set)
                          </label>
                          <div
                            className="map-container"
                            style={{ height: "300px", marginBottom: "1rem" }}
                          >
                            <Wrapper
                              apiKey={GOOGLE_MAPS_CONFIG.apiKey}
                              render={render}
                            >
                              <Map
                                center={
                                  selectedLocation.coordinates || {
                                    lat: 19.076,
                                    lng: 72.8777,
                                  }
                                }
                                zoom={selectedLocation.coordinates ? 15 : 12}
                                onMapClick={(loc) => {
                                  const newLocation = {
                                    ...selectedLocation,
                                    coordinates: loc,
                                    address: `Location at ${loc.lat.toFixed(
                                      4
                                    )}, ${loc.lng.toFixed(4)}`,
                                  };
                                  setSelectedLocation(newLocation);

                                  // Reverse geocode to get address details
                                  if (window.google && window.google.maps) {
                                    const geocoder =
                                      new window.google.maps.Geocoder();
                                    geocoder.geocode(
                                      { location: loc },
                                      (results, status) => {
                                        if (status === "OK" && results[0]) {
                                          const addressComponents =
                                            results[0].address_components;
                                          const city =
                                            addressComponents.find((comp) =>
                                              comp.types.includes("locality")
                                            )?.long_name || "";
                                          const state =
                                            addressComponents.find((comp) =>
                                              comp.types.includes(
                                                "administrative_area_level_1"
                                              )
                                            )?.long_name || "";

                                          setSelectedLocation((prev) => ({
                                            ...prev,
                                            city,
                                            state,
                                            address:
                                              results[0].formatted_address,
                                          }));
                                        }
                                      }
                                    );
                                  }
                                }}
                                locations={[
                                  ...formData.locations,
                                  ...(selectedLocation.coordinates
                                    ? [selectedLocation]
                                    : []),
                                ]}
                              />
                            </Wrapper>
                          </div>
                          {selectedLocation.coordinates && (
                            <div className="alert alert-info">
                              Selected location:{" "}
                              {selectedLocation.coordinates.lat.toFixed(4)},{" "}
                              {selectedLocation.coordinates.lng.toFixed(4)}
                            </div>
                          )}
                        </div>

                        <div className="d-grid mt-3">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleAddLocation}
                            disabled={
                              !selectedLocation.city ||
                              !selectedLocation.state ||
                              loading
                            }
                          >
                            Add Location to List
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Locations Table */}
                    {formData.locations.length > 0 && (
                      <div className="card mb-4">
                        <div className="card-header">
                          <h5 className="mb-0">Selected Locations</h5>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>City</th>
                                <th>State</th>
                                <th>Pincode Type</th>
                                <th>Pincodes</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formData.locations.map((location, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{location.city}</td>
                                  <td>{location.state}</td>
                                  <td>
                                    <span
                                      className={`badge ${location.pincodeType === "all"
                                        ? "bg-success"
                                        : "bg-primary"
                                        }`}
                                    >
                                      {location.pincodeType === "all"
                                        ? "All"
                                        : "Specific"}
                                    </span>
                                  </td>
                                  <td>
                                    {location.pincodeType === "all" ? (
                                      <span className="text-muted">
                                        All pincodes
                                      </span>
                                    ) : (
                                      <div
                                        className="d-flex flex-wrap gap-1"
                                        style={{ maxWidth: "200px" }}
                                      >
                                        {location.pincodes.map((pincode, i) => (
                                          <span
                                            key={i}
                                            className="badge bg-secondary me-1 mb-1"
                                          >
                                            {pincode}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        handleRemoveLocation(index)
                                      }
                                      disabled={loading}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="row mb-3">
                      {/* Submit */}
                      <div className="row mt-4">
                        <div className="col-md-12">
                          <button
                            type="submit"
                            className="btn btn-primary py-2"
                            disabled={
                              loading ||
                              errors.percentage ||
                              errors.redeemAfterDays
                            }
                          >
                            {loading ? "Processing..." : (rewardData ? "Update" : "Submit")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Category Modal */}
          <CategoryModal
            show={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            onSelect={handleCategorySelect}
            selectedId={formData.category_id}
          />

          {/* Subcategory Modal */}
          <SubcategoryModal
            show={showSubCategoryModal}
            onClose={() => setShowSubCategoryModal(false)}
            onSelect={handleSubCategorySelect}
            categoryId={formData.category_id}
            selectedId={formData.subCategory_id}
          />

          {/* Vehicle Type Modal */}
          <VehicleTypeModal
            show={showVehicleTypeModal}
            onClose={() => setShowVehicleTypeModal(false)}
            onSelect={handleVehicleTypeSelect}
            categoryId={formData.category_id}
            subcategoryId={formData.subCategory_id}
            selectedId={formData.vehicle_id}
          />
        </div>
      </div>
    </div>
  );
}